import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isSameDay } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Wrench,
  Sparkles,
  Sun,
  Sunset,
  Truck,
  RefreshCw,
  XCircle,
  ArrowLeft,
  MapPin,
} from 'lucide-react';
import { useState, useRef } from 'react';
import clsx from 'clsx';

export default function Booking() {
  const { customerId, jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Prefer ?sig= (current). Fall back to ?token= for legacy links still in flight.
  // Forward to backend as `sig` so DoubleTick's auto-appended ?token=<jwt> doesn't collide.
  const token = searchParams.get('sig') || searchParams.get('token');

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const timeRef = useRef(null);
  const dateScrollRef = useRef(null);
  const scrollDates = (dir) => dateScrollRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Fetch Availability
  const { data, isLoading, error } = useQuery({
    queryKey: ['availability', customerId, jobId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (token) params.append('sig', token);

      const res = await axios.get(
        `/api/booking/availability/${customerId}/${jobId}?${params}`,
      );
      return res.data;
    },
    retry: false,
  });

  // Confirm Booking Mutation
  const confirmMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        customerId,
        jobId,
        selectedDate: format(selectedDate, 'yyyy-MM-dd'),
        selectedTime,
        sig: token,
      };
      const res = await axios.post('/api/booking/confirm', payload);
      return res.data;
    },
    onSuccess: (data) => {
      navigate('/book/success', { state: { booking: data.booking, bookingUrl: window.location.href, customerId, jobId, token } });
    },
  });

  // Reschedule Booking Mutation
  const rescheduleMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        customerId,
        jobId,
        selectedDate: format(selectedDate, 'yyyy-MM-dd'),
        selectedTime,
        sig: token,
      };
      const res = await axios.post('/api/booking/reschedule', payload);
      return res.data;
    },
    onSuccess: (data) => {
      navigate('/book/success', { state: { booking: data.booking, isReschedule: true, bookingUrl: window.location.href, customerId, jobId, token } });
    },
  });

  // Cancel Booking Mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        customerId,
        jobId,
        sig: token,
      };
      const res = await axios.post('/api/booking/cancel', payload);
      return res.data;
    },
    onSuccess: () => {
      navigate('/book/cancelled', { state: { message: 'Your booking has been cancelled.' } });
    },
  });

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-light via-white to-green-50">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-brand-light rounded-full"></div>
            <div className="absolute inset-0 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
            <img src="/logo.png" alt="The Golf Buggy Guy" className="absolute inset-0 m-auto w-16 h-16 object-contain" />
          </div>
          <p className="text-brand-dark font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-brand-light p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl shadow-red-500/10 p-8 text-center border border-red-100">
          <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-brand-dark mb-3">
            Booking Unavailable
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            {error.response?.data?.error ||
              'This booking link is invalid or has expired. Please contact support for assistance.'}
          </p>
          {error.response?.data?.status && (
            <div className="bg-brand-light rounded-2xl p-4 text-sm">
              <span className="text-brand-neutral">Current Status: </span>
              <span className="font-bold text-brand-dark">
                {error.response.data.status}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const { job, availability, isScheduled, currentBooking, canReschedule, canCancel } = data;

  // If job is already scheduled and not in reschedule mode, show current booking
  if (isScheduled && !isRescheduling) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-light via-white to-green-50 pb-6">
        {/* Header */}
        <header className="bg-white/90 backdrop-blur-lg border-b border-brand-light">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="The Golf Buggy Guy" className="h-12 w-auto" />
              <div className="flex-1">
                <h1 className="text-lg font-bold text-brand-dark">
                  Your Booking
                </h1>
                <p className="text-sm text-brand-neutral">
                  {job.customerName}
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Current Booking Card */}
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl p-6 text-white shadow-lg shadow-brand-primary/20">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-8 h-8" />
              <div>
                <p className="text-white/80 text-xs font-medium uppercase tracking-wider">Status</p>
                <p className="font-bold text-xl">Confirmed</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-white/70" />
                <div>
                  <p className="text-white/70 text-xs">Date</p>
                  <p className="font-bold">{currentBooking?.dateDisplay || currentBooking?.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-white/70" />
                <div>
                  <p className="text-white/70 text-xs">Time</p>
                  <p className="font-bold">{currentBooking?.timeLabel || currentBooking?.time}</p>
                </div>
              </div>
              {job.collectionAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-white/70 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white/70 text-xs">Collection address</p>
                    <p className="font-bold">{job.collectionAddress}</p>
                    <p className="text-white/70 text-xs mt-1">
                      Not right? Please call us before your collection.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Service Info */}
          <div className="bg-white rounded-2xl p-4 border border-brand-light">
            <p className="text-xs text-brand-neutral uppercase tracking-wider mb-1">Service Type</p>
            <p className="font-bold text-brand-dark">{job.category}</p>
          </div>

          {job.collectionAddress && (
            <div className="bg-white rounded-2xl p-4 border border-brand-light">
              <p className="text-xs text-brand-neutral uppercase tracking-wider mb-1">Collection address</p>
              <p className="font-bold text-brand-dark">{job.collectionAddress}</p>
              <p className="text-xs text-brand-neutral mt-1">
                Not right? Please call us before your collection.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {canReschedule && (
              <button
                onClick={() => setIsRescheduling(true)}
                className="w-full bg-white border-2 border-brand-primary text-brand-primary font-bold py-4 px-6 rounded-2xl hover:bg-brand-light transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Reschedule Appointment
              </button>
            )}

            {canCancel && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="w-full bg-white border-2 border-red-400 text-red-600 font-bold py-4 px-6 rounded-2xl hover:bg-red-50 transition-all flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Cancel Booking
              </button>
            )}
          </div>
        </main>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 fade-in duration-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-2">Cancel Booking?</h3>
                <p className="text-brand-neutral text-sm">
                  Are you sure you want to cancel your appointment on {currentBooking?.dateDisplay}?
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="w-full bg-red-500 text-white font-bold py-3 px-6 rounded-xl hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                >
                  {cancelMutation.isPending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Cancelling...
                    </>
                  ) : (
                    'Yes, Cancel Booking'
                  )}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Keep My Booking
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Toast */}
        {cancelMutation.isError && (
          <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-red-500 text-white p-4 rounded-2xl shadow-lg z-30">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="font-medium text-sm">
                {cancelMutation.error?.response?.data?.error || 'Failed to cancel booking. Please try again.'}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Get slots for selected date
  const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const activeSlots = (availability || []).find((d) => d.date === selectedDateStr)?.slots || [];

  // Get category icon
  const getCategoryIcon = () => {
    if (job.category?.includes('Workshop')) return Wrench;
    return Wrench;
  };
  const CategoryIcon = getCategoryIcon();

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light via-white to-green-50 pb-32 md:pb-6">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-brand-light sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            {isRescheduling && (
              <button
                onClick={() => setIsRescheduling(false)}
                className="w-10 h-10 bg-brand-light rounded-xl flex items-center justify-center hover:bg-brand-light/80 transition-all"
              >
                <ArrowLeft className="w-5 h-5 text-brand-primary" />
              </button>
            )}
            <img src="/logo.png" alt="The Golf Buggy Guy" className="h-12 w-auto" />
            <div className="flex-1">
              <h1 className="text-lg font-bold text-brand-dark">
                {isRescheduling ? 'Reschedule Appointment' : 'Book Your Service'}
              </h1>
              <p className="text-sm text-brand-neutral">
                {job.customerName}
              </p>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            <div className={clsx(
              'flex-1 h-1.5 rounded-full transition-all duration-500',
              selectedDate ? 'bg-brand-primary' : 'bg-gray-200'
            )}></div>
            <div className={clsx(
              'flex-1 h-1.5 rounded-full transition-all duration-500',
              selectedTime ? 'bg-brand-primary' : 'bg-gray-200'
            )}></div>
            <div className="flex-1 h-1.5 rounded-full bg-gray-200"></div>
          </div>
          <div className="flex justify-between mt-1.5 text-[10px] font-medium text-brand-neutral uppercase tracking-wider">
            <span className={selectedDate ? 'text-brand-primary' : ''}>Date</span>
            <span className={selectedTime ? 'text-brand-primary' : ''}>Time</span>
            <span>Confirm</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Service Info Card */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-4 text-white shadow-lg shadow-brand-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <CategoryIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white/80 text-xs font-medium uppercase tracking-wider">Service Type</p>
                <p className="font-bold text-lg">{job.category}</p>
                {job.workOrderNumber && (
                  <p className="text-white/90 text-sm mt-0.5">Ref: {job.workOrderNumber}</p>
                )}
              </div>
            </div>
            {job.scheduledDate && (
              <div className="bg-white/20 backdrop-blur rounded-xl px-3 py-2 text-right">
                <p className="text-white/80 text-[10px] uppercase tracking-wider">Currently</p>
                <p className="font-semibold text-sm">{job.scheduledDate}</p>
              </div>
            )}
          </div>
        </div>

        {/* Date Selection */}
        <section className="bg-white rounded-3xl shadow-xl shadow-brand-primary/5 border border-brand-light">
          <div className="p-4 border-b border-brand-light bg-gradient-to-r from-brand-light to-white rounded-t-3xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <h2 className="font-bold text-brand-dark">Choose a Date</h2>
                <p className="text-xs text-brand-neutral">Select your preferred service date</p>
              </div>
            </div>
          </div>

          <div className="p-4 pt-6">
            {/* Horizontal Scrollable Date Cards */}
            <div className="relative">
              {/* Desktop scroll arrows (mobile users swipe the strip) */}
              <button
                type="button"
                aria-label="Previous dates"
                onClick={() => scrollDates(-1)}
                className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white/95 border border-gray-200 shadow-md text-brand-primary hover:bg-brand-light hover:border-brand-secondary transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                aria-label="Next dates"
                onClick={() => scrollDates(1)}
                className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white/95 border border-gray-200 shadow-md text-brand-primary hover:bg-brand-light hover:border-brand-secondary transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div
                ref={dateScrollRef}
                onWheel={(e) => { if (e.deltaY) e.currentTarget.scrollLeft += e.deltaY; }}
                className="flex gap-3 overflow-x-auto pb-2 pt-2 px-1 sm:px-11 snap-x snap-mandatory scrollbar-hide scroll-smooth"
              >
              {availability.map((day, index) => {
                const date = parseISO(day.date);
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                const slotsAvailable = day.slots.some((s) => s.available);
                const availableCount = day.slots.filter((s) => s.available).length;
                const isToday = isSameDay(date, new Date());
                const isClosed = !!day.blocked;

                return (
                  <button
                    key={day.date}
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedTime(null);
                      setTimeout(() => timeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                    }}
                    disabled={!slotsAvailable}
                    title={isClosed ? (day.blockedReason ? `Closed — ${day.blockedReason}` : 'Closed') : undefined}
                    className={clsx(
                      'flex-shrink-0 w-20 flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all duration-300 snap-center relative',
                      isSelected
                        ? 'border-brand-primary bg-brand-primary text-white shadow-lg shadow-brand-primary/30 scale-105'
                        : slotsAvailable
                          ? 'border-gray-200 bg-white hover:border-brand-secondary hover:shadow-md text-gray-700'
                          : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed',
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {isClosed && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">
                        Closed
                      </span>
                    )}
                    {isToday && !isClosed && (
                      <span className={clsx(
                        'absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full',
                        isSelected ? 'bg-white text-brand-primary' : 'bg-brand-light text-brand-primary'
                      )}>
                        Today
                      </span>
                    )}
                    <span className={clsx(
                      'text-[10px] font-bold uppercase tracking-wider',
                      isSelected ? 'text-white/80' : 'text-gray-400'
                    )}>
                      {format(date, 'EEE')}
                    </span>
                    <span className="text-2xl font-black mt-0.5">
                      {format(date, 'd')}
                    </span>
                    <span className={clsx(
                      'text-xs font-medium',
                      isSelected ? 'text-white/80' : 'text-gray-400'
                    )}>
                      {format(date, 'MMM')}
                    </span>
                    {slotsAvailable && (
                      <span className={clsx(
                        'text-[9px] font-bold mt-1.5 px-2 py-0.5 rounded-full',
                        isSelected
                          ? 'bg-white/20 text-white'
                          : 'bg-brand-light text-brand-primary'
                      )}>
                        Open
                      </span>
                    )}
                    {isSelected && (
                      <CheckCircle2 className="absolute -top-1.5 -right-1.5 w-5 h-5 text-white bg-brand-primary rounded-full" />
                    )}
                  </button>
                );
              })}
              </div>
            </div>
          </div>
        </section>

        {/* Time Selection - Morning/Afternoon Cards */}
        {selectedDate && (
          <section ref={timeRef} className="bg-white rounded-3xl shadow-xl shadow-brand-primary/5 border border-brand-light overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="p-4 border-b border-brand-light bg-gradient-to-r from-brand-light to-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-secondary/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-brand-secondary" />
                </div>
                <div>
                  <h2 className="font-bold text-brand-dark">Select a Time</h2>
                  <p className="text-xs text-brand-neutral">
                    {format(selectedDate, 'EEEE, MMMM d')}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {activeSlots.map((slot) => {
                const isMorning = parseInt(slot.time) < 12;
                const Icon = isMorning ? Sun : Sunset;
                const iconColor = isMorning ? 'text-amber-500' : 'text-orange-500';
                const isSelected = selectedTime === slot.time;

                if (!slot.available) {
                  return (
                    <div
                      key={slot.time}
                      className="p-5 rounded-2xl bg-gray-50 border-2 border-gray-100 opacity-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center">
                          <Icon className="w-7 h-7 text-gray-300" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-400 line-through text-lg">{slot.label}</p>
                          <p className="text-sm text-gray-400 mt-0.5">{slot.description || 'Not available'}</p>
                        </div>
                        <span className="text-xs font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Booked</span>
                      </div>
                    </div>
                  );
                }

                return (
                  <button
                    key={slot.time}
                    onClick={() => setSelectedTime(slot.time)}
                    className={clsx(
                      'w-full p-5 rounded-2xl border-2 transition-all duration-200 text-left relative',
                      isSelected
                        ? 'border-brand-primary bg-brand-light shadow-lg shadow-brand-primary/20 scale-[1.01]'
                        : 'border-gray-200 bg-white hover:border-brand-secondary hover:shadow-md',
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className={clsx(
                        'w-14 h-14 rounded-xl flex items-center justify-center',
                        isSelected ? 'bg-brand-primary/10' : 'bg-gray-50'
                      )}>
                        <Icon className={clsx('w-7 h-7', isSelected ? 'text-brand-primary' : iconColor)} />
                      </div>
                      <div className="flex-1">
                        <p className={clsx(
                          'font-bold text-lg',
                          isSelected ? 'text-brand-primary' : 'text-brand-dark'
                        )}>
                          {slot.label}
                        </p>
                        <p className={clsx(
                          'text-sm mt-0.5',
                          isSelected ? 'text-brand-secondary' : 'text-brand-neutral'
                        )}>
                          {slot.description || slot.label}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="absolute top-3 right-3 w-6 h-6 text-brand-primary" />
                    )}
                  </button>
                );
              })}

              {activeSlots.every((s) => !s.available) && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-brand-neutral" />
                  </div>
                  <p className="text-brand-dark font-medium">No times available on this date</p>
                  <p className="text-brand-neutral text-sm mt-1">Please select a different date</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Fallback Contact Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
          <p className="text-sm text-amber-800">
            If none of these times are convenient, please contact us on WhatsApp or phone{' '}
            <a href="tel:+971542249905" className="font-bold text-brand-primary underline">
              054 224 9905
            </a>
          </p>
        </div>

        {/* Selection Summary - Desktop */}
        {selectedDate && selectedTime && (
          <div className="hidden md:block bg-gradient-to-r from-brand-dark to-brand-primary rounded-3xl p-6 text-white shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-green-300" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">{isRescheduling ? 'New appointment' : 'Your appointment'}</p>
                  <p className="font-bold text-xl">
                    {format(selectedDate, 'EEE, MMM d')} at {activeSlots.find((s) => s.time === selectedTime)?.label}
                  </p>
                </div>
              </div>
              <button
                onClick={() => isRescheduling ? rescheduleMutation.mutate() : confirmMutation.mutate()}
                disabled={confirmMutation.isPending || rescheduleMutation.isPending}
                className="bg-white text-brand-primary font-bold py-3.5 px-8 rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
              >
                {(confirmMutation.isPending || rescheduleMutation.isPending) ? (
                  <>
                    <div className="w-5 h-5 border-2 border-brand-light border-t-brand-primary rounded-full animate-spin"></div>
                    {isRescheduling ? 'Rescheduling...' : 'Confirming...'}
                  </>
                ) : (
                  <>
                    {isRescheduling ? 'Confirm Reschedule' : 'Confirm Booking'}
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Mobile Floating Action Button */}
      {selectedDate && selectedTime && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-lg border-t border-brand-light shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.1)] z-20 animate-in slide-in-from-bottom duration-300">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl flex items-center justify-center text-white">
                <CalendarIcon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-brand-neutral uppercase tracking-wider font-medium">{isRescheduling ? 'New Date' : 'Selected'}</p>
                <p className="font-bold text-brand-dark">
                  {format(selectedDate, 'EEE, MMM d')} • {activeSlots.find((s) => s.time === selectedTime)?.label}
                </p>
              </div>
            </div>

            <button
              onClick={() => isRescheduling ? rescheduleMutation.mutate() : confirmMutation.mutate()}
              disabled={confirmMutation.isPending || rescheduleMutation.isPending}
              className="w-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-bold py-4 px-6 rounded-2xl shadow-lg shadow-brand-primary/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {(confirmMutation.isPending || rescheduleMutation.isPending) ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {isRescheduling ? 'Rescheduling...' : 'Confirming...'}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  {isRescheduling ? 'Confirm Reschedule' : 'Confirm Appointment'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {(confirmMutation.isError || rescheduleMutation.isError) && (
        <div className="fixed bottom-24 left-4 right-4 md:bottom-4 md:left-auto md:right-4 md:w-96 bg-red-500 text-white p-4 rounded-2xl shadow-lg z-30 animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="font-medium text-sm">
              {confirmMutation.error?.response?.data?.error || rescheduleMutation.error?.response?.data?.error || 'Failed to process booking. Please try again.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

